import dayjs from 'dayjs';
import { ICodeStatus, ICriticalInfo, IIdentityDocumentKind, IMedicaidStatus, ISurgery } from '../../caremap/ICareMap';

export interface ICriticalInfoDetail {
    text: string;
    // 'warning' flags a fact that needs acting on now: a lapsed or imminent date, or a code status
    // nobody has verified. 'confirmed' is its counterpart, someone did verify this and here is when.
    // Both render as chips rather than another grey line, because they answer the question the
    // reader came with. A date that is still far out carries no tone and stays plain.
    tone?: 'warning' | 'confirmed';
    // set only when the document is genuinely openable. A detail naming a document we cannot serve
    // stays plain text, so the affordance never promises something a click will not deliver
    href?: string;
}

// Renewing an ID or filing a redetermination takes weeks, so a date is only useful as a warning
// while there is still time to act on it. Flagging these only once they lapse would surface the
// problem at exactly the moment it has already caused harm.
const EXPIRY_WARNING_DAYS = 45;

export interface ICriticalInfoItem {
    text: string;
    details: ICriticalInfoDetail[];
}

export interface ICriticalInfoGroup {
    label: string;
    items: ICriticalInfoItem[];
}

const codeStatusLabels: Record<ICodeStatus, string> = {
    'full-code': 'Full code',
    'dnr': 'DNR',
    'dnr-with-limits': 'DNR with limits'
};

const medicaidStatusLabels: Record<IMedicaidStatus, string> = {
    'active': 'Active',
    'pending': 'Pending',
    'inactive': 'Inactive',
    'not-applicable': 'Not applicable'
};

const identityDocumentLabels: Record<IIdentityDocumentKind, string> = {
    'state-id': 'State ID',
    'drivers-license': "Driver's license",
    'passport': 'Passport'
};

/**
 * Turns the stored critical-info record into the lines the profile section renders.
 *
 * The provenance rules live here rather than in the component because they carry the meaning a case
 * manager reads the section for: a code status nobody has confirmed, and a date that has already
 * lapsed, both need to look different from a fact that is simply on file.
 */
export class CriticalInfoPresenter {
    static inject = () => [];

    private formatDate = (date: string) => dayjs(date).format('MMM D, YYYY');

    private hasLapsed = (date: string) => dayjs(date).isBefore(dayjs(), 'day');

    private daysUntil = (date: string) => dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day');

    // a date the caller must act on before it passes (an ID that stops proving identity, a policy
    // that stops paying, a redetermination that stops coverage), so it reads as an alert while
    // there is still time to do something about it, not only once the damage is done
    private expiryDetail = ({ date, futureLabel, lapsedLabel }: { date: string; futureLabel: string; lapsedLabel: string }): ICriticalInfoDetail => {
        if (this.hasLapsed(date)) {
            return { text: `${lapsedLabel} ${this.formatDate(date)}`, tone: 'warning' };
        }

        const days = this.daysUntil(date);
        if (days <= EXPIRY_WARNING_DAYS) {
            const remaining = days === 0 ? 'today' : days === 1 ? 'in 1 day' : `in ${days} days`;
            return { text: `${futureLabel} ${this.formatDate(date)}, ${remaining}`, tone: 'warning' };
        }

        return { text: `${futureLabel} ${this.formatDate(date)}` };
    };

    private buildCodeStatusGroup = (criticalInfo: ICriticalInfo): ICriticalInfoGroup | undefined => {
        const { codeStatus, codeStatusConfirmedOn, codeStatusSource } = criticalInfo;

        if (!codeStatus) {
            return undefined;
        }

        const details: ICriticalInfoDetail[] = [];

        // the whole point of the section for this audience: a status someone wrote down is not the
        // same as a status someone verified, and the difference has to be visible without digging
        if (codeStatusConfirmedOn) {
            details.push({ text: `Confirmed ${this.formatDate(codeStatusConfirmedOn)}`, tone: 'confirmed' });
        } else {
            details.push({ text: 'Referenced but not confirmed', tone: 'warning' });
        }

        if (codeStatusSource) {
            details.push({ text: codeStatusSource });
        }

        const document = criticalInfo.codeStatusDocument;
        if (document?.fileName) {
            // three states, and the difference is the whole point of the section for this audience:
            // a file we can open, a file that exists somewhere else, and a file nobody can produce
            if (document.url) {
                details.push({ text: document.fileName, href: document.url });
            } else if (document.storedIn) {
                details.push({ text: `${document.fileName} in ${document.storedIn}` });
            } else {
                details.push({ text: document.fileName });
            }
        }

        return { label: 'Code status', items: [{ text: codeStatusLabels[codeStatus], details }] };
    };

    private buildInsuranceGroup = (criticalInfo: ICriticalInfo): ICriticalInfoGroup | undefined => {
        const { insuranceCarrier, insurancePlan, insuranceMemberId, insuranceExpiresOn } = criticalInfo;

        if (!insuranceCarrier && !insurancePlan && !insuranceMemberId && !insuranceExpiresOn) {
            return undefined;
        }

        const details: ICriticalInfoDetail[] = [];

        if (insuranceMemberId) {
            details.push({ text: `Member ID ending ${insuranceMemberId}` });
        }

        if (insuranceExpiresOn) {
            details.push(this.expiryDetail({ date: insuranceExpiresOn, futureLabel: 'Expires', lapsedLabel: 'Expired' }));
        }

        const heading = [insuranceCarrier, insurancePlan].filter(Boolean).join(', ');

        return { label: 'Insurance', items: [{ text: heading || 'On file', details }] };
    };

    private buildMedicaidGroup = (criticalInfo: ICriticalInfo): ICriticalInfoGroup | undefined => {
        const { medicaidStatus, medicaidRedeterminationOn } = criticalInfo;

        if (!medicaidStatus && !medicaidRedeterminationOn) {
            return undefined;
        }

        const details: ICriticalInfoDetail[] = [];

        if (medicaidRedeterminationOn) {
            details.push(this.expiryDetail({
                date: medicaidRedeterminationOn,
                futureLabel: 'Redetermination due',
                lapsedLabel: 'Redetermination overdue since'
            }));
        }

        return {
            label: 'Medicaid',
            items: [{ text: medicaidStatus ? medicaidStatusLabels[medicaidStatus] : 'On file', details }]
        };
    };

    private buildDrugPlanGroup = (criticalInfo: ICriticalInfo): ICriticalInfoGroup | undefined => {
        if (!criticalInfo.drugPlan) {
            return undefined;
        }

        return { label: 'Drug plan', items: [{ text: criticalInfo.drugPlan, details: [] }] };
    };

    private buildIdentityGroup = (criticalInfo: ICriticalInfo): ICriticalInfoGroup | undefined => {
        const document = criticalInfo.identityDocument;

        if (!document || (!document.kind && !document.number && !document.state && !document.expiresOn)) {
            return undefined;
        }

        const { kind, number, state, expiresOn } = document;

        const headingParts = [kind ? identityDocumentLabels[kind] : 'Identification'];
        if (number) {
            headingParts.push(`ending ${number}`);
        }
        if (state) {
            headingParts.push(`(${state})`);
        }

        const details: ICriticalInfoDetail[] = [];
        if (expiresOn) {
            details.push(this.expiryDetail({ date: expiresOn, futureLabel: 'Expires', lapsedLabel: 'Expired' }));
        }

        return { label: 'Identification', items: [{ text: headingParts.join(' '), details }] };
    };

    private buildSurgeryItem = (surgery: ISurgery): ICriticalInfoItem => {
        const details: ICriticalInfoDetail[] = [];

        if (surgery.performedOn) {
            details.push({ text: this.formatDate(surgery.performedOn) });
        }

        if (surgery.notes) {
            details.push({ text: surgery.notes });
        }

        return { text: surgery.procedure || 'Procedure', details };
    };

    private buildSurgeriesGroup = (criticalInfo: ICriticalInfo): ICriticalInfoGroup | undefined => {
        const surgeries = (criticalInfo.surgeries || []).filter(s => s.procedure || s.performedOn || s.notes);

        if (surgeries.length === 0) {
            return undefined;
        }

        return { label: 'Surgical history', items: surgeries.map(this.buildSurgeryItem) };
    };

    getGroups = (criticalInfo?: ICriticalInfo): ICriticalInfoGroup[] => {
        if (!criticalInfo) {
            return [];
        }

        const groups = [
            this.buildCodeStatusGroup(criticalInfo),
            this.buildInsuranceGroup(criticalInfo),
            this.buildMedicaidGroup(criticalInfo),
            this.buildDrugPlanGroup(criticalInfo),
            this.buildIdentityGroup(criticalInfo),
            this.buildSurgeriesGroup(criticalInfo)
        ];

        return groups.filter((group): group is ICriticalInfoGroup => Boolean(group));
    };
}
